import React, { useContext, useEffect, useState } from 'react';
import { Button, Col, Row, Table } from 'reactstrap';
import { AiOutlineArrowLeft, AiOutlineArrowRight } from 'react-icons/ai';
import { toast } from "react-toastify";
import Switch from "react-switch";
import _ from 'lodash';
import {
  FilterDateTime,
  FilterSelect,
  FilterText,
  ItemDropdownMenu,
  ItemTypeahead,
  PODropdownMenu,
  ServiceThreadSpinner,
  VendorTypeahead,
  SortHeader
} from './';
import { DesktopContainer } from '../layouts';
import { api, constants, dateHelpers, filterHelpers, helpers, UserContext } from '../utils';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

var today = new Date();
var oneYearAgo = dateHelpers.formatDateForServer((new Date(today)).setFullYear(today.getFullYear() - 1));

export default function POReport(props) {
  const userCtx = useContext(UserContext);
  const { currentUser } = userCtx;
  const [working, setWorking] = useState(false);
  const [refData, setRefData] = useState({});
  const [data, setData] = useState([]);
  const [byItemNumber, setByItemNumber] = useState(false);
  const [filters, setFilters] = useState([
     { filterName: "startPODate", value: oneYearAgo },
  ]);
  const [sortField, setSortField] = useState("documentDate");
  const [sortDirection, setSortDirection] = useState("asc");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
     api
      .fetch('Reference/GetGLAccountList')
      .then(r => {
        if (r.data.success) {
          let list = _.chain(r.data.message)
          .map(gla => ({ label: gla.accountNumberWithDescription, value: gla.accountIndex, index: parseInt(gla.accountNumber.replace(/-/g, "")) }))
          .orderBy(gla => gla.index)
          .value()
          if(!list) toast.error("gla account list is empty");
          setRefData({glAccounts: list})
        }
      })
      .catch(api.catchHandler)
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
      onFilterChange({ filterName: "itemDescription", value: "" })
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byItemNumber]);

  useEffect(() => {
    if (loaded) {
      refreshData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortDirection]);

  function createPayload() {
    const payload = _.reduce(filters, (payload, filter) => {
      if (filter.filterName === "vendor") {
        payload.vendorId = filter.value.value
      } else if (filter.filterName === "itemNumber") {
        payload.itemNumber = filter.value.value
      } else {
        payload[filter.filterName] = filter.value;
      }
      return payload;
    }, {});
    payload.SortField = sortField;
    payload.SortDirection = sortDirection; 
    return payload;
  }

  const exportXlsx = () => {
    if (!data?.length) return;
    const payload = createPayload();
    setWorking(true);
    api.postGetBlob("Purchasing/ExportPOReportXLSX", payload)
      .then(r => {
        if (r.data.success === false) {
          if (_.isString(r?.data?.message)) {
            toast.error(r?.data?.message);
          }
          return;
        }
        if (r.data) {
          let XLSX_FILENAME = `PO_Report_${dateHelpers.toLongDateString(
            dateHelpers.monthDayAndYear(Date.now())
          )}.xlsx`;
          helpers.downloadExport(r.data, XLSX_FILENAME, constants.MIME_XLSX)
        } else {
          toast.error(r.message || "Error downloading report");
        }
      }).catch(api.catchHandler)
      .finally(() => setWorking(false))

  };

  function onFilterChange(changedFilter) {
    const update = filterHelpers.getFilters(filters, changedFilter);
    setFilters(update);
  }

  function onSortChange(field, direction) {
    if (field !== sortField) {
      setSortField(field);
      setSortDirection("asc");
    } else {
      setSortDirection(direction);
    }
  }

  function refreshData() { 
    const payload = createPayload();
    if (_.isEmpty(payload)) {
      toast.warning("Please provide at least one search parameter");
      return;
    }
    setWorking(true);
    api
      .fetch('Purchasing/POSearch', createPayload())
      .then(r => {
        if (r.data.success) {
          setData(r.data.message);
          setLoaded(true);
        } else {
          toast.error(r.data.message);
        }
      })
      .catch(api.catchHandler)
      .finally(() => setWorking(false))

  }

  function runReport() {
    const payload = createPayload();
    if (_.isEmpty(payload)) {
      toast.warning("Please provide at least one search parameter");
      return;
    }
    setWorking(true);
    api.postDownloadBinary(`Report/POSummary`, payload, constants.MIME_PDF, `PO_Report_Summary_${dateHelpers.toLongDateString(dateHelpers.monthDayAndYear(Date.now()))}.pdf`)
    .then((res) => {
      if(res?.data === false) {
        toast.error(res.message);
      }
    })
    .catch(api.catchHandler)
    .finally(() => setWorking(false))
  }

  return (
    document.title = `PO Report`,
    <DesktopContainer screenName="PO Report">
      <div className="filter-header mb-4">
        <Row className="align-items-center my-1">
          <Col xs={3}>
            <h5 className="text-white float-end">Vendor:</h5>
          </Col>
          <Col xs={6}>
            <VendorTypeahead
              value={filterHelpers.getValue(filters, "vendor")}
              onChange={opt => onFilterChange({ filterName: "vendor", value: opt })}
            />
          </Col>
        </Row>
        <Row className="align-items-center my-1">
          <Col xs={3}>
            <h5 className="text-white float-end">Account Number:</h5>
          </Col>
          <Col xs={6}>
            <FilterSelect
              filterName="accountIndex"
              options={refData.glAccounts}
              value={filterHelpers.getValue(filters, "accountIndex")}
              onChangeCallback={onFilterChange}
              isClearable
            />
          </Col>
        </Row>
        <Row className="align-items-center my-1">
          <Col xs={3} className="text-end">
            <h5 className="d-inline text-white me-2">Item Number</h5>
            <Switch
              checked={byItemNumber}
              onChange={checked => setByItemNumber(checked)}
              onColor="#f5f5cc"
              offColor="#f5f5cc"
              onHandleColor="#f76f1e"
              offHandleColor="#f76f1e"
              uncheckedIcon={<AiOutlineArrowLeft className="ms-1 mt-1" />}
              checkedIcon={<AiOutlineArrowRight className="ms-2 mt-1" />}
            />
            <h5 className="d-inline text-white ms-2">Item Description:</h5>
          </Col>
          <Col xs={6}>
            {byItemNumber
              ? <FilterText
                filterName="itemDescription"
                placeholder="Type a partial item description"
                value={filterHelpers.getValue(filters, "itemDescription")}
                onChangeCallback={onFilterChange}
              />
              : <ItemTypeahead
                value={filterHelpers.getValue("itemNumber")}
                onChange={opt => onFilterChange({ filterName: "itemNumber", value: opt })}
              />
            }
          </Col>
        </Row>
        <Row className="align-items-center my-1">
          <Col xs={3}>
            <h5 className="text-white float-end">PO Number:</h5>
          </Col>
          <Col xs={6}>
            <FilterText
              filterName="poNumber"
              value={filterHelpers.getValue(filters, "poNumber")}
              onChangeCallback={onFilterChange}
            />
          </Col>
        </Row>
        <Row className="align-items-center my-1">
          <Col xs={3}>
            <h5 className="text-white float-end">Start PO Date:</h5>
          </Col>
          <Col xs={6}>
            <FilterDateTime
              filterName="startPODate"
              value={filterHelpers.getValue(filters, "startPODate")}
              onChangeCallback={onFilterChange}
            />
          </Col>
        </Row>
        <Row className="align-items-center my-1">
          <Col xs={3}>
            <h5 className="text-white float-end">End PO Date:</h5>
          </Col>
          <Col xs={6}>
            <FilterDateTime
              filterName="endPODate"
              value={filterHelpers.getValue(filters, "endPODate")}
              onChangeCallback={onFilterChange}
            />
          </Col>
        </Row>
        <Row className="mt-3">
          <Col xs={{ offset: 2, size: 4 }} className="text-center">
            <Button
              className="shredButton w-100 me-4"
              onClick={refreshData}
              disabled={working}
            >
              Show PO's
            </Button>
          </Col>
          <Col xs={4} className="text-center">
            <Button
              className="shredButton w-100"
              onClick={runReport}
              disabled={working}
            >
              Run Report
            </Button>
          </Col>
        </Row>
      </div>
      <ServiceThreadSpinner on={working}>
        {!_.isNil(data) && data.length > 0 &&
        <>
          <Row className={"mb-2"}>
            <Col
              xs={{ offset: 6, size: 6 }}
              className="transparent-background"
            >
                <Button
                  title="Download as .xlsx"
                  className="shredButtonExcel float-end ms-2"
                  onClick={exportXlsx}
                  disabled={working}
                >
                  <FontAwesomeIcon icon="file-excel" size="lg" />
                </Button>
            </Col>
          </Row>
          <Table size="sm" className="data-table">
            <thead className="table-header">
              <tr>
                <th>                  
                  <SortHeader 
                  displayName="Date"
                  field="documentDate"
                  sortDirection={sortDirection}
                  sorted={sortField === "documentDate"}
                  callback={onSortChange}
                  />
                </th>
                <th>                  
                  <SortHeader 
                  displayName="PO Number"
                  field="poNumber"
                  sortDirection={sortDirection}
                  sorted={sortField === "poNumber"}
                  callback={onSortChange}
                  />
                </th>
                <th>                  
                  <SortHeader 
                  displayName="Item"
                  field="itemNumber"
                  sortDirection={sortDirection}
                  sorted={sortField === "itemNumber"}
                  callback={onSortChange}
                  />
                </th>
                <th>                  
                  <SortHeader 
                  displayName="Vendor Item"
                  field="vendorItem"
                  sortDirection={sortDirection}
                  sorted={sortField === "vendorItem"}
                  callback={onSortChange}                  
                  />
                </th>
                <th>                  
                  <SortHeader 
                  displayName="Vendor"
                  field="vendorId"
                  sortDirection={sortDirection}
                  sorted={sortField === "vendorId"}
                  callback={onSortChange}
                  />
                </th>
                <th className="text-end">                  
                  <SortHeader 
                  displayName="Qty Ordered"
                  field="orderQty"
                  sortDirection={sortDirection}
                  sorted={sortField === "orderQty"}
                  callback={onSortChange}
                  />
                </th>
                <th className="text-end">                  
                  <SortHeader 
                  displayName="Unit Cost"
                  field="unitCost"
                  sortDirection={sortDirection}
                  sorted={sortField === "unitCost"}
                  callback={onSortChange}
                  />
                </th>
                <th className="text-end pe-3">                  
                  <SortHeader 
                  displayName="Ext. Cost"
                  field="extendedCost"
                  sortDirection={sortDirection}
                  sorted={sortField === "extendedCost"}
                  callback={onSortChange}
                  />
                </th>
                <th>                  
                  <SortHeader
                  displayName="Account #"
                  field="accountNum"
                  sortDirection={sortDirection}
                  sorted={sortField === "accountNum"}
                  callback={onSortChange}
                  />
                </th>
                <th>                  
                  <SortHeader 
                  displayName="PO status"
                  field="lineStatusText"
                  sortDirection={sortDirection}
                  sorted={sortField === "lineStatusText"}
                  callback={onSortChange}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {_.map(data, (datum, idx) => {
                return (
                  <tr key={idx} className={`tr-${idx % 2 === 0 ? "primary" : "secondary"}`}>
                    <td>{dateHelpers.toLongDateString(datum.documentDate)}</td>
                    <PODropdownMenu
                      poNumber={datum.poNumber}
                      attachmentCount={datum.poAttachmentCount}
                      showAddContainer
                      showAddCost={currentUser?.isAdmin || currentUser?.isPowerUser || currentUser?.isPurchasingEdit}
                      itemNumber={datum.stItemNumber}
                      containers={datum.containers}
                      callbackRefresh={() => refreshData()}
                    />
                    <ItemDropdownMenu
                      itemNumber={datum.stItemNumber}
                      itemDescription={datum.stItemWithDescription}
                      attachmentCount={datum.itemAttachmentCount}
                    />
                    {/* <td>{datum.stItemWithDescription}</td> */}
                    <td>{datum.vendorItemWithDescription}</td>
                    <td>{datum.vendorId}</td>
                    <td className="text-end">{helpers.formatWithDecimalPlaces(datum.orderQty, 5)}</td>
                    <td className="text-end">{helpers.formatCurrency(datum.unitCost)}</td>
                    <td className="text-end pe-3">{helpers.formatCurrency(datum.extendedCost)}</td>
                    <td>{datum.accountNum}</td>
                    <td>{datum.lineStatusText}</td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot className="table-footer">
              <tr>
                <td colSpan={5} />
                <td className="fw-bold text-end">{helpers.formatWithDecimalPlaces(_.sumBy(data, datum => datum.orderQty), 5)}</td>
                <td />
                <td className="fw-bold text-end pe-3">{helpers.formatCurrency(_.sumBy(data, datum => datum.extendedCost))}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </Table>
          </>
        }        
      </ServiceThreadSpinner>
    </DesktopContainer>
  );
}